/**
 * The MECH sheet's **PROTOTYPE WEAPON** block in EDIT (`ll1-readiness` 1.9): the Engineer's Full
 * Repair settings for the talent's integrated Prototype Weapon (`t_engineer`, Core p.98), the
 * browser half of `lvtt roster prototype`. Every control dispatches `set_mech_prototype` with the
 * whole merged {@link PrototypeConfig}, since the command replaces the record.
 *
 * - **TYPE** and **DAMAGE** (every rank): the weapon type (Melee takes Threat 1, the rest Range 10)
 *   and damage type. Pressing the held chip unpicks it: an unpicked type keeps the printed Threat 1
 *   and Range 10, an unpicked damage type the per-attack Variable pick.
 * - **LIMITED**: the rolled uses. ROLL posts the rank's notation (1d6+2, 2d6 at rank 3) to COMMS
 *   through the host's `rollDice`, so the table sees the card, and takes the server's total off it;
 *   the stepper sets the value by hand (a physical roll, or a GM's fix). Unrolled, the frozen weapon
 *   keeps 1 use (`tagVal` truncates `"1d6+2"`), and the block says so.
 * - **REVISIONS** (rank 2 and up): choose two of Tweaked Optics, Tweaked Computer and Stripped
 *   Reactor Shielding. `set_mech_prototype` takes exactly two, so a lone pick is a local draft until
 *   the second; a pressed chip of a held pair unpicks it into that draft.
 *
 * Shown while the pilot holds Engineer, or while a stale record remains (so it can be cleared).
 *
 * **The re-roll prompt** (`ll1-readiness` 3.4, {@link PrototypeRerollBand}): a band across the MECH
 * sheet, in and out of EDIT, for the owner or the GM, while the Limited is owed: never rolled, or
 * marked `rerollDue` by a Full Repair (Core p.98, "each time you perform a Full Repair, reroll").
 * Its ROLL is the block's ROLL; REVIEW opens EDIT for the type, damage and Revisions RAW lets the
 * Full Repair change. Only a new Limited value clears `rerollDue`; other edits keep it.
 */
import { useState } from "react";
import {
  CHOSEN_DAMAGE_TYPES,
  PROTOTYPE_REVISIONS,
  PROTOTYPE_WEAPON_TYPES,
  type MechData,
  type PilotData,
  type PrototypeConfig,
  type PrototypeRevision,
  type RosterCommand,
} from "@lancer-vtt/domain";
import { Stepper } from "./brutalistRows";

export const ENGINEER_TALENT_ID = "t_engineer";

const REVISION_LABEL: Record<PrototypeRevision, string> = { optics: "TWEAKED OPTICS", computer: "TWEAKED COMPUTER", shielding: "STRIPPED SHIELDING" };
const REVISION_HINT: Record<PrototypeRevision, string> = {
  optics: "+1 Accuracy on attacks with the prototype weapon",
  computer: "The prototype weapon is Smart",
  shielding: "Each attack may take an area option at the cost of 2 Heat",
};

/** The Limited roll for an Engineer rank: its notation and the totals it can land on. */
export function prototypeLimitedRoll(rank: number): { notation: string; min: number; max: number } {
  return rank >= 3 ? { notation: "2d6", min: 2, max: 12 } : { notation: "1d6+2", min: 3, max: 8 };
}

/** Rolls a notation in COMMS and resolves with the card's total (`usePostedRoll`), `null` on a refusal or timeout. */
export type RollDice = (notation: string, label: string) => Promise<number | null>;

type PrototypeProps = {
  pilot: PilotData;
  mech: MechData;
  actorId: string;
  dispatch: (cmd: RosterCommand) => void;
  /** The host's COMMS roll; absent (no game session), ROLL is not offered. */
  rollDice: RollDice | undefined;
};

/**
 * The prototype record's writer, shared by the block and the band: `set` sends the whole merged
 * record (keeping `rerollDue`), `setUses` a new Limited (dropping it), `roll` the COMMS roll.
 */
function usePrototypeSettings({ pilot, mech, actorId, dispatch, rollDice }: PrototypeProps) {
  const [rolling, setRolling] = useState(false);
  const [rollFailed, setRollFailed] = useState(false);
  const rank = pilot.talents[ENGINEER_TALENT_ID] ?? 0;
  const config = mech.prototype;
  const limited = prototypeLimitedRoll(rank);

  const set = (next: PrototypeConfig, { rolled = false } = {}) => {
    const clean: PrototypeConfig = {
      ...(next.weaponType !== undefined ? { weaponType: next.weaponType } : {}),
      ...(next.damageType !== undefined ? { damageType: next.damageType } : {}),
      ...(next.revisions !== undefined ? { revisions: next.revisions } : {}),
      ...(next.limitedUses !== undefined ? { limitedUses: next.limitedUses } : {}),
      ...(next.rerollDue && !rolled ? { rerollDue: true as const } : {}),
    };
    dispatch({ type: "set_mech_prototype", actorId, pilotId: pilot.id, mechId: mech.id, ...(Object.keys(clean).length > 0 ? { prototype: clean } : {}) });
  };
  const setUses = (n: number) => set({ ...config, limitedUses: n }, { rolled: true });

  async function roll() {
    if (!rollDice) return;
    setRolling(true);
    setRollFailed(false);
    const total = await rollDice(limited.notation, `PROTOTYPE LIMITED · ${(mech.name || "MECH").toUpperCase()}`);
    setRolling(false);
    if (total === null) return setRollFailed(true);
    setUses(Math.max(1, total));
  }
  return { rank, config, limited, set, setUses, roll, rolling, rollFailed };
}

/** The Limited is owed: an Engineer's prototype never rolled, or marked due by a Full Repair. */
export function prototypeRollOwed(pilot: PilotData, mech: MechData): "unrolled" | "full-repair" | null {
  if ((pilot.talents[ENGINEER_TALENT_ID] ?? 0) === 0) return null;
  if (mech.prototype?.limitedUses === undefined) return "unrolled";
  return mech.prototype.rerollDue ? "full-repair" : null;
}

const ROLL_FAILED = "The roll did not come back. Check the link, or set the uses by hand.";

/**
 * The MECH sheet's re-roll prompt (`ll1-readiness` 3.4): the owed Limited and a ROLL, with REVIEW
 * into EDIT while EDIT is off. Renders nothing while nothing is owed.
 */
export function PrototypeRerollBand(props: PrototypeProps & { onReview: (() => void) | undefined }) {
  const { limited, roll, rolling, rollFailed } = usePrototypeSettings(props);
  const owed = prototypeRollOwed(props.pilot, props.mech);
  if (owed === null) return null;
  const uses = props.mech.prototype?.limitedUses;
  return (
    <div className="unit-sheet-notactive unit-sheet-reroll" role="status" aria-label="Prototype re-roll">
      <span>
        {owed === "full-repair" ? `FULL REPAIR · RE-ROLL THE PROTOTYPE'S LIMITED (NOW ${uses})` : "PROTOTYPE · ROLL ITS LIMITED USES (1 UNTIL ROLLED)"}
      </span>
      {props.rollDice && (
        <button type="button" className="unit-sheet-action" disabled={rolling} onClick={() => void roll()}>
          {rolling ? "ROLLING…" : `ROLL ${limited.notation.toUpperCase()}`}
        </button>
      )}
      {props.onReview && (
        <button type="button" className="unit-sheet-action" title="Change the weapon type, damage type or Revisions" onClick={props.onReview}>
          REVIEW
        </button>
      )}
      {rollFailed && <span className="unit-sheet-prototype-note is-warn">{ROLL_FAILED}</span>}
    </div>
  );
}

export function MechPrototypeEdit(props: PrototypeProps) {
  const { pilot, mech, rollDice } = props;
  const [draft, setDraft] = useState<PrototypeRevision | null>(null);
  const { rank, config, limited: roll, set, setUses, roll: onRoll, rolling, rollFailed } = usePrototypeSettings(props);
  const uses = config?.limitedUses;
  const revisions = config?.revisions ?? [];
  const owed = prototypeRollOwed(pilot, mech);

  const onRevision = (r: PrototypeRevision) => {
    if (revisions.includes(r)) {
      // Unpick from a held pair: the other stays as the draft, and the record drops its Revisions.
      setDraft(revisions.find((x) => x !== r) ?? null);
      set({ ...config, revisions: undefined });
    } else if (draft === r) {
      setDraft(null);
    } else if (draft !== null) {
      setDraft(null);
      set({ ...config, revisions: PROTOTYPE_REVISIONS.filter((x) => x === draft || x === r) });
    } else if (revisions.length === 2) {
      // A third pick replaces the pair: it becomes the draft, waiting for its partner.
      setDraft(r);
      set({ ...config, revisions: undefined });
    } else {
      setDraft(r);
    }
  };

  return (
    <div className="unit-sheet-prototype">
      <div className="unit-sheet-prototype-row">
        <span className="unit-sheet-field-label">TYPE · {config?.weaponType === undefined ? "THREAT 1 AND RANGE 10" : config.weaponType === "Melee" ? "THREAT 1" : "RANGE 10"}</span>
        <div className="unit-sheet-btnrow" role="group" aria-label="Weapon type">
          {PROTOTYPE_WEAPON_TYPES.map((t) => {
            const on = config?.weaponType === t;
            return (
              <button key={t} type="button" className={`unit-sheet-mini${on ? " is-picked" : ""}`} aria-pressed={on} onClick={() => set({ ...config, weaponType: on ? undefined : t })}>
                {t.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
      <div className="unit-sheet-prototype-row">
        <span className="unit-sheet-field-label">DAMAGE · {config?.damageType === undefined ? "VARIABLE, PICKED PER ATTACK" : config.damageType.toUpperCase()}</span>
        <div className="unit-sheet-btnrow" role="group" aria-label="Damage type">
          {CHOSEN_DAMAGE_TYPES.map((d) => {
            const on = config?.damageType === d;
            return (
              <button key={d} type="button" className={`unit-sheet-mini${on ? " is-picked" : ""}`} aria-pressed={on} onClick={() => set({ ...config, damageType: on ? undefined : d })}>
                {d.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
      <div className="unit-sheet-prototype-row">
        <span className="unit-sheet-field-label">LIMITED · {roll.notation.toUpperCase()}</span>
        <Stepper
          display={uses !== undefined ? String(uses) : "—"}
          label="Limited uses"
          canDec={uses !== undefined && uses > 1}
          canInc={uses === undefined || uses < roll.max}
          onDec={() => uses !== undefined && setUses(uses - 1)}
          onInc={() => setUses(uses === undefined ? roll.min : uses + 1)}
        />
        {rollDice && (
          <button type="button" className="unit-sheet-mini" disabled={rolling} title="Roll in COMMS; the total sets the uses" onClick={() => void onRoll()}>
            {rolling ? "ROLLING…" : `ROLL ${roll.notation.toUpperCase()}`}
          </button>
        )}
      </div>
      {uses === undefined && <div className="unit-sheet-prototype-note is-warn">NOT ROLLED · 1 USE IN PLAY</div>}
      {owed === "full-repair" && <div className="unit-sheet-prototype-note is-warn">RE-ROLL DUE · FULL REPAIR</div>}
      {rollFailed && <div className="unit-sheet-prototype-note is-warn">{ROLL_FAILED}</div>}
      {rank >= 2 && (
        <div className="unit-sheet-prototype-row">
          <span className="unit-sheet-field-label">REVISIONS · CHOOSE TWO</span>
          <div className="unit-sheet-btnrow" role="group" aria-label="Revisions">
            {PROTOTYPE_REVISIONS.map((r) => {
              const on = revisions.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  className={`unit-sheet-mini${on ? " is-picked" : draft === r ? " is-draft" : ""}`}
                  aria-pressed={on || draft === r}
                  title={REVISION_HINT[r]}
                  onClick={() => onRevision(r)}
                >
                  {REVISION_LABEL[r]}
                </button>
              );
            })}
          </div>
          {draft !== null && <div className="unit-sheet-prototype-note">PICK ONE MORE</div>}
        </div>
      )}
    </div>
  );
}
